import { Box, Button, Flex, Modal, Table, Tag, Text, useDisclosure } from '@lego/klik-ui';
import { useEffect } from 'react';
import { DetailsHeader } from '@/components/common';
import { Loader } from '@/components/common/Loader';
import type { RolloutWindowResponse } from '@/hooks/rollout_windows';
import type {
  DeviceRolloutSummary,
  FleetRolloutWindowStats,
} from '@/hooks/rollout_windows/fleet_rollout_window_stats';
import { dateTimeFormat } from '@/utility/date';

type Props = {
  rolloutStats: FleetRolloutWindowStats;
};

type StatTileProps = {
  label: string;
  value: number;
  total: number;
  color: string;
  description: string;
};

const StatTile = ({ label, value, total, color, description }: StatTileProps) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Box borderColor="gray.100" borderRadius="md" borderWidth="1px" flex="1" minW="140px" p={4}>
      <Text color="gray.500" fontSize="xs" fontWeight="medium" mb={1} textTransform="uppercase">
        {label}
      </Text>
      <Flex align="baseline" gap={2}>
        <Text color={color} fontSize="2xl" fontWeight="bold" lineHeight="1">
          {value}
        </Text>
        <Text color="gray.400" fontSize="sm">
          / {total}
        </Text>
        <Text color="gray.400" fontSize="xs">
          ({pct}%)
        </Text>
      </Flex>
      <Text color="gray.500" fontSize="xs" mt={1}>
        {description}
      </Text>
    </Box>
  );
};

const windowStatusTag = (windows: RolloutWindowResponse[]) => {
  if (windows.length === 0) {
    return (
      <Tag colorScheme="gray" size="sm">
        Not configured
      </Tag>
    );
  }
  if (windows.some((w) => w.status === 'ACTIVE')) {
    return (
      <Tag colorScheme="green" size="sm">
        Active
      </Tag>
    );
  }
  return (
    <Tag colorScheme="light-blue" size="sm">
      Configured
    </Tag>
  );
};

type DeviceDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  deviceSummaries: DeviceRolloutSummary[];
};

const DeviceDetailsModal = ({ isOpen, onClose, deviceSummaries }: DeviceDetailsModalProps) => (
  <Modal isOpen={isOpen} onClose={onClose} size="xl">
    <Modal.Overlay />
    <Modal.Content>
      <Modal.Header>Device Rollout Window Coverage</Modal.Header>
      <Modal.CloseButton />
      <Modal.Body>
        <Table size="sm" variant="simple">
          <Table.Head>
            <Table.Row>
              <Table.ColumnHeader>Device</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Windows</Table.ColumnHeader>
              <Table.ColumnHeader>Earliest window</Table.ColumnHeader>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {deviceSummaries.map(({ device, rolloutWindows }) => {
              const earliest = rolloutWindows.map((w) => w.startTime ?? w.createdAt).sort()[0];
              return (
                <Table.Row key={device.id}>
                  <Table.Cell fontWeight="medium">{device.name}</Table.Cell>
                  <Table.Cell>{windowStatusTag(rolloutWindows)}</Table.Cell>
                  <Table.Cell>{rolloutWindows.length}</Table.Cell>
                  <Table.Cell color="gray.500" fontSize="sm">
                    {earliest ? new Date(earliest).toLocaleString(dateTimeFormat.local) : '—'}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} variant="outline">
          Close
        </Button>
      </Modal.Footer>
    </Modal.Content>
  </Modal>
);

export const FleetRolloutCoverageCard = ({ rolloutStats }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { configured, active, total, isLoading, failedCount, deviceSummaries, loadStats } =
    rolloutStats;

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return (
    <>
      <Box background="white" boxShadow="md" padding={6} rounded="md" width="100%">
        <Flex align="flex-start" justify="space-between" mb={4}>
          <DetailsHeader title="Fleet Rollout Coverage" />
          {!isLoading && total > 0 && (
            <Button onClick={onOpen} size="sm" variant="outline">
              View details
            </Button>
          )}
        </Flex>

        <Text color="gray.500" fontSize="sm" mb={4}>
          Rollout window status across all {total} device{total !== 1 ? 's' : ''} in this fleet
        </Text>

        {isLoading ? (
          <Loader />
        ) : total === 0 ? (
          <Text color="gray.500" fontSize="sm">
            No devices are assigned to this fleet.
          </Text>
        ) : (
          <>
            {failedCount > 0 && (
              <Text color="orange.500" fontSize="xs" mb={2}>
                ⚠ Could not fetch rollout windows for {failedCount} device
                {failedCount !== 1 ? 's' : ''}. Stats may be incomplete.
              </Text>
            )}
            <Flex gap={4} wrap="wrap">
              <StatTile
                color="green.600"
                description="Have at least one rollout window scheduled"
                label="Configured"
                total={total}
                value={configured}
              />
              <StatTile
                color="light-blue.600"
                description="Have an open rollout window right now"
                label="Active window"
                total={total}
                value={active}
              />
            </Flex>
          </>
        )}
      </Box>

      <DeviceDetailsModal deviceSummaries={deviceSummaries} isOpen={isOpen} onClose={onClose} />
    </>
  );
};
